<!-- @format -->

<!--
  Site-wide announcement bar rendered ONLY from a server-authored experiment
  payload (surface "promo_banner", bead zpe.2). Lets the store agent run
  launch/seasonal promos with zero deploys: no assignment, no payload, or no
  text → NOTHING renders (today's state and the control arm).

  Dismissal is persisted per campaign (localStorage, keyed by
  promo_banner:{experimentKey}:{variant}) so a new campaign or variant
  reappears while the one the visitor closed stays closed. Exposure is a bare
  `exposure` event, emitted at most ONCE per page load (module-level latch) and
  only when a variant with text actually resolves and is not dismissed.
-->

<template>
  <div v-if="banner" class="promo-banner" :class="toneClass" role="status">
    <span class="promo-banner__text">{{ banner.text }}</span>
    <RouterLink
      v-if="banner.link && banner.link.startsWith('/')"
      class="promo-banner__link"
      :to="banner.link"
      >{{ banner.link_text || "Learn more" }}</RouterLink
    >
    <a v-else-if="banner.link" class="promo-banner__link" :href="banner.link">{{
      banner.link_text || "Learn more"
    }}</a>
    <button
      type="button"
      class="promo-banner__close"
      aria-label="Dismiss announcement"
      @click="dismiss"
    >
      &times;
    </button>
  </div>
</template>

<script lang="ts">
/** @format */

// The tone literals live in @outstanding/shared, where the API's create-time
// zod enum reads the same array (bead outstanding-9rgp.10).
import { oneOf, PROMO_BANNER_TONES } from "@outstanding/shared";
import type { PromoBannerTone } from "@outstanding/shared";
import { boundedString, isPlainObject } from "@/lib/experimentPayload";
import { resetExperimentSurfaceExposureForTests } from "@/composables/useExperimentSurface";

// The experiment surface this component consumes. The experiment KEY stays
// server-side data (resolved via getAssignmentBySurface), not code.
export const PROMO_BANNER_SURFACE = "promo_banner";

/**
 * Server-authored promo_banner variant payload contract:
 *
 *   { text: string, link?: string, link_text?: string, tone?: "info" | "promo" }
 *
 * `text` is required — without it nothing renders. `link` is an internal path
 * (RouterLink when it starts with "/") or an absolute http(s) URL (plain
 * anchor); any other scheme is dropped defensively. `tone` picks the (quiet)
 * color treatment and defaults to "info".
 */
export interface PromoBannerPayload {
  text: string;
  link?: string;
  link_text?: string;
  tone?: PromoBannerTone;
}

// Defensive bounds so a mis-authored payload can't blow up the layout.
const TEXT_MAX_LEN = 300;
const LINK_MAX_LEN = 500;
const LINK_TEXT_MAX_LEN = 100;

/**
 * Validate + normalize a raw (unknown) variant payload. Returns null unless it
 * is an object with a non-blank string `text` — malformed payloads render
 * nothing rather than breaking the layout. Links are kept only when they are
 * an internal path ("/...") or an absolute http(s) URL.
 */
export function parsePromoBannerPayload(raw: unknown): PromoBannerPayload | null {
  if (!isPlainObject(raw)) return null;
  const text = boundedString(raw.text, TEXT_MAX_LEN);
  if (!text) return null;

  const payload: PromoBannerPayload = { text };

  const link = boundedString(raw.link, LINK_MAX_LEN);
  if (link) {
    // Internal path or absolute http(s) only — anything else (javascript:,
    // data:, protocol-relative, …) is dropped; the text still renders.
    if (link.startsWith("/") || /^https?:\/\//i.test(link)) payload.link = link;
  }
  if (payload.link) {
    const linkText = boundedString(raw.link_text, LINK_TEXT_MAX_LEN);
    if (linkText) payload.link_text = linkText;
  }
  payload.tone = oneOf(raw.tone, PROMO_BANNER_TONES) ?? "info";
  return payload;
}

/**
 * The localStorage key persisting a dismissal of one campaign/variant.
 * Lifecycle (versioned envelope, 90-day TTL, legacy raw-timestamp migration)
 * is owned by promoDismissalPolicy in utils/clientState (outstanding-qbo.10):
 * a dismissed campaign stays closed for 90 days, then may reappear.
 */
export function promoBannerDismissalKey(experimentKey: string, variant: string): string {
  return `promo_banner:${experimentKey}:${variant}`;
}

/** Test hook: simulate a fresh page load for the exposure latch. */
export function resetPromoBannerExposureLatchForTests(): void {
  resetExperimentSurfaceExposureForTests(PROMO_BANNER_SURFACE);
}
</script>

<script setup lang="ts">
/** @format */

import { computed, onMounted, ref } from "vue";
import { useExperimentSurface } from "@/composables/useExperimentSurface";
import {
  promoDismissalPolicy,
  readClientState,
  writeClientState,
} from "@/utils/clientState";

// Resolved banner (null → render nothing). Starts null and only ever flips to
// a value once assignments resolve, so there is no flash-and-remove: the bar
// may appear after hydration, but never renders then disappears.
const banner = ref<PromoBannerPayload | null>(null);
const dismissalKey = ref<string>("");

const toneClass = computed(() =>
  banner.value?.tone === "promo" ? "promo-banner--promo" : "promo-banner--info",
);

function isDismissed(key: string): boolean {
  // readClientState never throws (private mode / disabled → not dismissed)
  // and enforces the 90-day dismissal TTL: an expired dismissal reads null,
  // so a long-running campaign the visitor closed re-pitches after 90 days.
  return readClientState(promoDismissalPolicy(key)) !== null;
}

function dismiss(): void {
  banner.value = null;
  // Never throws — non-fatal: the banner just reappears next load.
  writeClientState(promoDismissalPolicy(dismissalKey.value), Date.now());
}

onMounted(async () => {
  try {
    // App.vue kicks the fetch off at app level (bead zpe.7); this awaits the
    // same memoized promise, so the banner costs no extra request.
    const match = await useExperimentSurface(PROMO_BANNER_SURFACE, {
      oncePerLoad: true,
    });
    if (!match) return;
    // Exposure fires for EVERY resolved arm — control (null payload) and
    // dismissed included — once per page load. Exposure means "entered the
    // experiment on a page where the banner would render", not "saw the bar";
    // a control arm that never logs exposure makes the z-test permanently
    // insignificant (2026-07-04 solidity review; matches the Cart.vue rule).
    const key = promoBannerDismissalKey(match.key, match.variant);
    const payload = parsePromoBannerPayload(match.payload);
    if (!payload) return; // control arm / malformed payload → nothing renders
    if (isDismissed(key)) return; // dismissed → no render
    dismissalKey.value = key;
    banner.value = payload;
  } catch {
    // Best-effort: any failure just leaves the bar unrendered.
  }
});
</script>

<style scoped>
/** @format */

/* Visually quiet cousin of the shipping banner above it: same centered,
   thin-bar treatment, slightly smaller type, tone-tinted background. */
.promo-banner {
  position: relative;
  text-align: center;
  padding: 6px 44px;
  font-size: 0.9rem;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.promo-banner--info {
  background-color: #eef1f4; /* soft neutral blue-gray */
  color: #4a4a4a;
}

.promo-banner--promo {
  background-color: #fdecd5; /* the shipping banner's soft peach */
  color: #5a5a5a;
}

.promo-banner__link {
  margin-left: 8px;
  color: inherit;
  font-weight: 600;
  text-decoration: underline !important;
}

.promo-banner__close {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 1.15rem;
  line-height: 1;
  padding: 4px 10px;
  cursor: pointer;
  opacity: 0.55;
}

.promo-banner__close:hover {
  opacity: 1;
}
</style>
